import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { useTour } from "@/components/tour/TourProvider";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { OperationsTask } from "@shared/schema";
import { HostCRM, TeamMember, formatCents, formatDate, statusColors } from "./_shared";



// ── Hosts CRM Tab ─────────────────────────────────────────────────────────────

export interface HostProperty {
  propertyId: number;
  address: string | null;
  name: string | null;
  assignedTeamMemberId: number | null;
}


export function HostsTab({ vendorId, isOwner, canManageAssignments, owningHostId, onSubViewChange, openDetailRequest, onConsumeOpenDetail }: { vendorId: number; isOwner: boolean; canManageAssignments: boolean; owningHostId?: number | null; onSubViewChange?: (sub: "list" | "hostDetail") => void; openDetailRequest?: boolean; onConsumeOpenDetail?: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedHostId, setSelectedHostId] = useState<number | null>(null);
  const { progress: tourProgress, isProgressLoading: tourProgressLoading, hasTour, startTour, activeTour } = useTour();
  const hasAutoStartedDetailRef = useRef(false);

  // Sync the open/closed sub-view up to the dashboard so the Guide button
  // can flip between the list-level and detail-level Hosts tours.
  useEffect(() => {
    onSubViewChange?.(selectedHostId === null ? "list" : "hostDetail");
  }, [selectedHostId, onSubViewChange]);

  // First time the vendor opens any host card, auto-start the detail tour
  // — owners specifically tend not to discover the per-property Auto-assign
  // dropdown on their own. Honors the same persistence record as every other
  // tour: if the vendor has already completed or skipped it, this is a no-op.
  useEffect(() => {
    if (selectedHostId === null) return;
    if (hasAutoStartedDetailRef.current) return;
    if (tourProgressLoading) return;
    if (activeTour) return;
    if (!hasTour("hostsDetail")) return;
    const record = tourProgress["hostsDetail"];
    if (record && (record.status === "completed" || record.status === "skipped")) {
      hasAutoStartedDetailRef.current = true;
      return;
    }
    // Brief delay so the detail view's targets have rendered before the
    // engine measures them (tour engine retries, but starting after layout
    // settles avoids the visible "spotlight jumps into place" flicker).
    const t = window.setTimeout(() => {
      hasAutoStartedDetailRef.current = true;
      startTour("hostsDetail");
    }, 400);
    return () => window.clearTimeout(t);
  }, [selectedHostId, tourProgress, tourProgressLoading, activeTour, hasTour, startTour]);

  const { data: hosts = [], isLoading } = useQuery<HostCRM[]>({
    queryKey: ["vendor", vendorId, "hosts"],
    queryFn: async () => {
      const r = await fetch("/api/vendor-portal/hosts", { credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || `Request failed: ${r.status}`);
      return r.json();
    },
  });

  // The Guidebook can start the per-host detail tour from any screen. When it
  // does it raises `openDetailRequest`; drill into the first host card so the
  // detail tour has targets to spotlight, then clear the request. Waits for the
  // host list to load (the tab may have just mounted) and is a no-op when a card
  // is already open or when there are no hosts (the tour engine then renders a
  // centred tooltip instead).
  useEffect(() => {
    if (!openDetailRequest) return;
    if (selectedHostId !== null) {
      onConsumeOpenDetail?.();
      return;
    }
    if (hosts.length === 0) return;
    setSelectedHostId(hosts[0].hostId);
    onConsumeOpenDetail?.();
  }, [openDetailRequest, hosts, selectedHostId, onConsumeOpenDetail]);

  const { data: teamMembers = [] } = useQuery<TeamMember[]>({
    queryKey: ["vendor", vendorId, "team"],
    queryFn: async () => {
      const r = await fetch("/api/vendor-portal/team", { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: selectedHostId !== null,
  });

  const { data: hostProperties = [], isLoading: propsLoading } = useQuery<HostProperty[]>({
    queryKey: ["vendor", vendorId, "hosts", selectedHostId, "properties"],
    queryFn: async () => {
      const r = await fetch(`/api/vendor-portal/hosts/${selectedHostId}/properties`, { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: selectedHostId !== null,
  });

  const { data: hostTasks = [], isLoading: hostTasksLoading } = useQuery<OperationsTask[]>({
    queryKey: ["vendor", vendorId, "hosts", selectedHostId, "tasks"],
    queryFn: async () => {
      const r = await fetch(`/api/vendor-portal/hosts/${selectedHostId}/tasks`, { credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || `Request failed: ${r.status}`);
      return r.json();
    },
    enabled: selectedHostId !== null,
  });

  const assignMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ propertyId, teamMemberId }: { propertyId: number; teamMemberId: number | null }) => {
      return apiRequest("PUT", `/api/vendor-portal/property-assignments/${propertyId}`, { teamMemberId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "hosts", selectedHostId, "properties"] });
      toast({ title: "Assignment saved" });
    },
    onError: (e: Error) => toast({ title: "Failed to save assignment", description: parseErrorMessage(e, "Unknown error"), variant: "destructive" }),
  });

  if (selectedHostId !== null) {
    const host = hosts.find(h => h.hostId === selectedHostId);
    const activeMembers = teamMembers.filter(m => m.status === "active");
    return (
      <div className="space-y-4">
        <button
          className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
          onClick={() => setSelectedHostId(null)}
          data-testid="button-back-to-hosts"
        >
          <ArrowLeft className="h-4 w-4" />Back to Hosts
        </button>
        {host && (
          <Card>
            <CardContent className="p-4">
              <p className="font-semibold text-gray-900">{host.hostName}</p>
              {host.businessName && <p className="text-sm text-gray-500">{host.businessName}</p>}
              {host.hostEmail && <p className="text-xs text-gray-400">{host.hostEmail}</p>}
              {host.hostPhone && <p className="text-xs text-gray-400">{host.hostPhone}</p>}
              <div className="flex gap-4 mt-3 pt-3 border-t text-xs text-gray-500">
                <span>Total jobs: <strong className="text-gray-900">{host.totalJobs}</strong></span>
                <span>Open: <strong className="text-gray-900">{host.openTaskCount}</strong></span>
                <span>Last: <strong className="text-gray-900">{formatDate(host.lastJobDate)}</strong></span>
              </div>
            </CardContent>
          </Card>
        )}
        <div className="space-y-2" data-testid="section-host-properties" data-tour="hosts-detail-properties">
          <h4 className="font-medium text-gray-700 text-sm">Properties & Team Assignments</h4>
          {!propsLoading && hostProperties.length === 0 && (
            <p className="text-sm text-gray-400 py-2" data-testid="text-no-properties">This host hasn't marked any properties for you yet.</p>
          )}
          {hostProperties.length > 0 && (
            <>
            {canManageAssignments && <p className="text-xs text-gray-400">Set which team member handles tasks at each property by default.</p>}
            {hostProperties.map(prop => (
              <Card key={prop.propertyId} data-testid={`card-host-property-${prop.propertyId}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-gray-900 truncate">{prop.name || prop.address || `Property #${prop.propertyId}`}</p>
                      {prop.name && prop.address && <p className="text-xs text-gray-400 truncate">{prop.address}</p>}
                    </div>
                    {canManageAssignments ? (
                      <Select
                        value={prop.assignedTeamMemberId ? String(prop.assignedTeamMemberId) : "none"}
                        onValueChange={val => assignMutation.mutate({ propertyId: prop.propertyId, teamMemberId: val === "none" ? null : parseInt(val, 10) })}
                      >
                        <SelectTrigger className="w-40 h-8 text-xs" data-testid={`select-property-assignee-${prop.propertyId}`}>
                          <SelectValue placeholder="Auto-assign" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Auto-assign</SelectItem>
                          {activeMembers.map(m => (
                            <SelectItem key={m.id} value={String(m.id)}>
                              {m.firstName} {m.lastName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs text-gray-400 shrink-0">
                        {prop.assignedTeamMemberId
                          ? (activeMembers.find(m => m.id === prop.assignedTeamMemberId)?.firstName || "Assigned")
                          : "Auto-assign"}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            </>
          )}
        </div>
        <div className="space-y-2" data-testid="section-host-task-history" data-tour="hosts-detail-task-history">
          <h4 className="font-medium text-gray-700 text-sm">Task History</h4>
          {hostTasksLoading ? (
            <p className="text-sm text-gray-400 text-center py-4">Loading tasks...</p>
          ) : hostTasks.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No tasks for this host.</p>
          ) : (
            <div className="space-y-2">
              {hostTasks.map(t => (
                <Card key={t.id} data-testid={`card-host-task-${t.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-gray-900">{t.title}</p>
                        <p className="text-xs text-gray-400">{formatDate(t.createdAt)}</p>
                      </div>
                      <div className="text-right">
                        <Badge className={statusColors[t.status] || "bg-gray-100 text-gray-800"} variant="secondary">
                          {t.status.replace("_", " ")}
                        </Badge>
                        {t.priceCents && <p className="text-xs font-medium mt-0.5">{formatCents(t.priceCents)}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const firstOpenHostIdx = hosts.findIndex(h => h.openTaskCount > 0);

  return (
    <div className="space-y-3" data-tour="hosts-list">
      <h3 className="font-semibold text-gray-900">Host Clients</h3>
      {isLoading ? (
        <p className="text-sm text-gray-400 text-center py-8">Loading hosts...</p>
      ) : hosts.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">No host clients yet. Tasks assigned to you will appear here.</p>
      ) : (
        hosts.map((host, idx) => (
          <Card
            key={host.hostId}
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setSelectedHostId(host.hostId)}
            data-testid={`card-host-${host.hostId}`}
            data-tour={idx === 0 ? "hosts-host-card" : undefined}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-900">{host.hostName}</p>
                    {owningHostId != null && host.hostId === owningHostId && (
                      <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 border-blue-200" data-testid={`badge-invited-you-${host.hostId}`}>Invited you</Badge>
                    )}
                  </div>
                  {host.businessName && <p className="text-xs text-gray-500">{host.businessName}</p>}
                  <div className="flex gap-3 mt-1 text-xs text-gray-400">
                    <span><strong className="text-gray-700">{host.totalJobs}</strong> total jobs</span>
                    {host.openTaskCount > 0 && (
                      <span
                        className="text-blue-600 font-medium"
                        data-tour={idx === firstOpenHostIdx ? "hosts-open-tasks-link" : undefined}
                      >
                        {host.openTaskCount} open
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{formatDate(host.lastJobDate)}</span>
                  <ChevronRight className="h-4 w-4 text-gray-300" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
